'use client';

import { cn } from '@/shared/utils/formatting';
import { User } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';

interface AvatarProps {
  src?: string | null | undefined;
  alt?: string | null | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | undefined;
  className?: string | undefined;
  fallback?: string | null | undefined;
}

const sizes = {
  xs: 'h-5 w-5',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16',
  xxl: 'h-40 w-40',
};

const imageSizes = {
  xs: 40,
  sm: 48,
  md: 80,
  lg: 96,
  xl: 128,
  xxl: 320,
};

export function Avatar({ src, alt = 'Avatar', size = 'md', className, fallback }: AvatarProps) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [src]);

  const initials = fallback
    ?.split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className={cn(
        'relative rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center',
        sizes[size],
        className
      )}
    >
      {src && !imgError ? (
        <Image
          src={src}
          alt={alt ?? 'Avatar'}
          width={imageSizes[size]}
          height={imageSizes[size]}
          className="object-cover w-full h-full"
          onError={() => setImgError(true)}
          quality={100}
        />
      ) : initials ? (
        <span className="text-gray-600 dark:text-gray-300 font-medium text-sm">{initials}</span>
      ) : (
        <User className="w-1/2 h-1/2 text-gray-400 dark:text-gray-500" />
      )}
    </div>
  );
}
